import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Calendar, Download, Search } from 'lucide-react';

const todayStr = () => new Date().toISOString().substring(0, 10);
const offsetStr = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().substring(0, 10);
};
const firstOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().substring(0, 10);
};

/**
 * Filtro de período reutilizável.
 * Quick buttons: Hoje, Ontem, 7d, Este mês, Custom.
 * Props: value={start,end}, onChange({start,end}), onExportCsv?
 */
export const DateRangeFilter = ({ value, onChange, onExportCsv, exportLabel = 'Exportar CSV', className = '' }) => {
  const [draft, setDraft] = React.useState({ start: value.start, end: value.end });
  React.useEffect(() => { setDraft({ start: value.start, end: value.end }); }, [value.start, value.end]);

  const setRange = (start, end) => onChange({ start, end });
  const isDirty = draft.start !== value.start || draft.end !== value.end;
  const applyDraft = () => {
    if (!draft.start || !draft.end) return;
    const start = draft.start <= draft.end ? draft.start : draft.end;
    const end = draft.start <= draft.end ? draft.end : draft.start;
    onChange({ start, end });
  };

  const isToday = value.start === todayStr() && value.end === todayStr();
  const isYesterday = value.start === offsetStr(-1) && value.end === offsetStr(-1);
  const is7 = value.start === offsetStr(-6) && value.end === todayStr();
  const isMonth = value.start === firstOfMonth() && value.end === todayStr();

  const presetBtn = (active, label, onClick, testid) => (
    <Button
      type="button"
      size="sm"
      variant={active ? 'default' : 'outline'}
      onClick={onClick}
      data-testid={testid}
    >
      {label}
    </Button>
  );

  return (
    <div className={`flex flex-wrap items-end gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3 ${className}`} data-testid="date-range-filter">
      <div className="flex items-center gap-2 mr-2">
        <Calendar size={16} className="text-slate-600" />
        <span className="text-sm font-semibold text-slate-700">Período</span>
      </div>
      {presetBtn(isToday, 'Hoje', () => setRange(todayStr(), todayStr()), 'preset-today')}
      {presetBtn(isYesterday, 'Ontem', () => setRange(offsetStr(-1), offsetStr(-1)), 'preset-yesterday')}
      {presetBtn(is7, 'Últimos 7d', () => setRange(offsetStr(-6), todayStr()), 'preset-7d')}
      {presetBtn(isMonth, 'Este mês', () => setRange(firstOfMonth(), todayStr()), 'preset-month')}
      <div className="flex items-center gap-1 ml-2">
        <span className="text-xs text-slate-500">De</span>
        <Input
          type="date"
          value={draft.start}
          max={todayStr()}
          onChange={(e) => setDraft((d) => ({ ...d, start: e.target.value }))}
          onKeyDown={(e) => { if (e.key === 'Enter') applyDraft(); }}
          className="h-8 text-sm w-[140px]"
          data-testid="range-start"
        />
        <span className="text-xs text-slate-500">até</span>
        <Input
          type="date"
          value={draft.end}
          max={todayStr()}
          onChange={(e) => setDraft((d) => ({ ...d, end: e.target.value }))}
          onKeyDown={(e) => { if (e.key === 'Enter') applyDraft(); }}
          className="h-8 text-sm w-[140px]"
          data-testid="range-end"
        />
        <Button
          type="button"
          size="sm"
          onClick={applyDraft}
          disabled={!draft.start || !draft.end}
          className={isDirty ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}
          variant={isDirty ? 'default' : 'outline'}
          data-testid="range-apply-btn"
        >
          <Search size={14} className="mr-1" /> Buscar
        </Button>
      </div>
      {onExportCsv && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onExportCsv}
          className="ml-auto"
          data-testid="export-csv-btn"
        >
          <Download size={14} className="mr-1" /> {exportLabel}
        </Button>
      )}
    </div>
  );
};

/**
 * Helper para gerar e baixar CSV.
 * columns: [{ key, label, format? }]
 */
export const downloadCsv = (filename, rows, columns) => {
  const escape = (v) => {
    if (v == null) return '';
    const s = String(v).replace(/"/g, '""');
    return /[",\n;]/.test(s) ? `"${s}"` : s;
  };
  const header = columns.map((c) => escape(c.label)).join(';');
  const lines = rows.map((r) =>
    columns.map((c) => {
      const raw = typeof c.key === 'function' ? c.key(r) : r[c.key];
      const val = c.format ? c.format(raw, r) : raw;
      return escape(val);
    }).join(';')
  );
  const csv = '\uFEFF' + [header, ...lines].join('\n'); // BOM para Excel ler acentos
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
